import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import Badge from '@repo/ui/badge'
import Typography from '@repo/ui/typography'
import { TFunction } from 'i18next'

import { CurrencyEnum } from '../../../../lib/types/enums/common/currency.enum'
import { RoleEnum } from '../../../../lib/types/enums/users/role.enum'
import {
  TravelExpense,
  TravelExpenseInput
} from '../../../../lib/types/models/travel-expenses/travel-expenses'
import { EditableTableColumn } from '../../../elements/editable-table'

export type TravelExpenseApiRequest = Omit<TravelExpenseInput, 'createdByUserRole' | 'id'> & {
  id?: number
}

export type UpdateTravelExpensesCurrencyInput = {
  currency: CurrencyEnum
}

export function mapTravelExpensesToInput(expenses: TravelExpense): TravelExpenseInput {
  return {
    id: expenses.id,
    type: expenses.type,
    date: expenses.date,
    details: expenses.details,
    amount: expenses.amount,
    document: expenses.document,
    validated: expenses.validated,
    createdByUserRole: expenses.createdBy.role,
    validatedAmount: expenses.validatedAmount
  }
}

export function mapTravelExpensesInputToRequest(e: TravelExpenseInput): TravelExpenseApiRequest {
  return {
    ...(e.id > 0 ? { id: e.id } : {}),
    type: e.type,
    date: e.date,
    details: e.details,
    amount: e.amount,
    document: e.document,
    validated: e.validated,
    validatedAmount: e.validatedAmount
  }
}

export const getTravelExpensesColumns = (
  t: TFunction<AvailableLanguages, undefined>,
  editableFields: (keyof TravelExpenseInput)[],
  mode: 'staff' | 'admin'
): EditableTableColumn<TravelExpenseInput>[] => [
  {
    header: t('staff.personal.space.my.journeys.details.expenses.table.header.date'),
    accessorKey: 'date',
    inputType: 'date',
    inputWrapperClassName: 'w-40',
    isEditable: editableFields.includes('date'),
    isEditableCondition: (te) =>
      !te.validated &&
      ((mode === 'admin' && te.createdByUserRole === RoleEnum.ADMIN) ||
        (mode === 'staff' && te.createdByUserRole === RoleEnum.STAFF)),
    cell: ({ row }) => (
      <Badge text={valueRender(row.original.date, (d) => DateFormatter.DDMMYYYY(d))} />
    )
  },
  {
    header: t('staff.personal.space.my.journeys.details.expenses.table.header.details'),
    accessorKey: 'details',
    inputType: 'text',
    inputWrapperClassName: 'w-60',
    cellClassName: 'max-w-96 text-wrap p-2',
    inputClassName: 'text-left px-2',
    isEditable: editableFields.includes('details'),
    isEditableCondition: (te) =>
      !te.validated &&
      ((mode === 'admin' && te.createdByUserRole === RoleEnum.ADMIN) ||
        (mode === 'staff' && te.createdByUserRole === RoleEnum.STAFF)),
    cell: ({ row }) => (
      <Typography variant="secondary-body">{valueRender(row.original.details)}</Typography>
    )
  },
  {
    header:
      mode === 'admin'
        ? t('staff.personal.space.my.journeys.details.expenses.table.header.amount.staff')
        : t('staff.personal.space.my.journeys.details.expenses.table.header.amount'),
    accessorKey: 'amount',
    centerCell: true,
    isEditable: editableFields.includes('amount'),
    isEditableCondition: (te) => !te.validated && mode === 'staff',
    inputType: 'number',
    cell: ({ row }) => {
      const amount = row.original.amount
      if (amount === null || amount === undefined) return '-'
      return <Badge text={amount.toFixed(2)} variant="darkBlue" size="xsmall" />
    }
  },
  {
    header:
      mode === 'admin'
        ? t('staff.personal.space.my.journeys.details.expenses.table.header.amount.lec')
        : t('staff.personal.space.my.journeys.details.expenses.table.header.amount'),
    accessorKey: 'validatedAmount',
    centerCell: true,
    hidden: mode === 'staff',
    isEditable: editableFields.includes('validatedAmount'),
    isEditableCondition: (te) => !te.validated && mode === 'admin',
    inputType: 'number',
    cell: ({ row }) => {
      const validatedAmount = row.original.validatedAmount
      if (validatedAmount === null || validatedAmount === undefined) return '-'
      return <Badge text={validatedAmount.toFixed(2)} variant="darkBlue" size="xsmall" />
    }
  }
]
